import { RETRY_DELAY_MS, sleep } from 'app/util/handleEvents';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ErrorResponseData } from '../types/errors';
import {
  DEFAULT_LANGUAGE,
  ERROR_NETWORK_CODE,
  REQUEST_TIMEOUT_MS,
  TIMEOUT_ERROR_CODES,
  ErrorStatus,
  MAX_RETRIES,
} from '../constants/errors';
import { buildInternalErrorDTO, getInternalErrorMessage, parseSpecialErrors } from '../utils/errorHandlers';

interface RetryConfig extends InternalAxiosRequestConfig {
  __retryCount?: number;
  retryOnTimeout?: boolean;
}

export interface RequestConfig extends AxiosRequestConfig {
  /**
   * Retry this request when it times out. Off by default and deliberately per-request: a
   * timeout says the response was lost, not that the server ignored the request, so replaying
   * one that has side effects (an order capture, say) risks doing the work twice. Set it only
   * where repeating the call is known to be safe.
   */
  retryOnTimeout?: boolean;
}

/**
 * Without this axios waits forever (its default is 0), so a connection that stalls without
 * failing never rejects and never retries -- the caller just keeps awaiting. Generous on
 * purpose: this exists to bound a hang, not to police latency, and a bound tight enough to
 * cut off a slow-but-working authorization would create the failure it is meant to prevent.
 */
export const axiosInstance = axios.create({ timeout: REQUEST_TIMEOUT_MS });

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorResponseData>) => {
    const languageHeader = error.config?.headers?.['Accept-Language'] || '';
    const language = languageHeader.split('-')[0] || DEFAULT_LANGUAGE;

    const config = error.config as RetryConfig;
    if (!config) {
      return parseErrors(error, language);
    }

    config.__retryCount = config.__retryCount || 0;

    if (isRetryableError(error, config) && config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1;
      await sleep(RETRY_DELAY_MS);
      return axiosInstance.request(config);
    }

    return parseErrors(error, language);
  },
);

export const isTimeoutError = (error: AxiosError<ErrorResponseData>) =>
  !!error.code && TIMEOUT_ERROR_CODES.includes(error.code);

// Retry if the response is empty, is an internal server error or the error code is ERR_NETWORK
const isRetryableError = (error: AxiosError<ErrorResponseData>, config?: RetryConfig) => {
  // A timeout leaves the outcome unknown, so it only retries where the caller has said
  // repeating the request is safe.
  if (isTimeoutError(error)) {
    return config?.retryOnTimeout === true;
  }
  return (
    error.response?.status === ErrorStatus.EMPTY_RESPONSE ||
    (error.response?.status && error.response?.status >= ErrorStatus.INTERNAL_SERVER_ERROR) ||
    error.code === ERROR_NETWORK_CODE
  );
};

const parseErrors = (error: AxiosError<ErrorResponseData>, language: string) => {
  const errorMessage = getInternalErrorMessage(language);

  // Returned before the 4xx branch below, which rewrites the message to the business one --
  // it would tell someone whose request stalled that their card was declined. Without this
  // the raw axios text ("timeout of 60000ms exceeded") is what reaches the customer.
  // Rejects with the backend's payload shape, like every other path in this module: consumers
  // read `.message` off it, and handleEvents forwards it to the embedding merchant through
  // JSON.parse(JSON.stringify(...)) -- where a bare Error serialises to `{}`, because its own
  // properties are not enumerable. Making just this branch an Error would break that payload
  // for a rule this repo does not even enable.
  if (isTimeoutError(error)) {
    return Promise.reject(buildInternalErrorDTO(errorMessage).data);
  }

  if (error.response?.status && error.response.status >= ErrorStatus.INTERNAL_SERVER_ERROR) {
    error.response.data.message = errorMessage;
  }

  if (error.code === ERROR_NETWORK_CODE) {
    const internalError: AxiosResponse<ErrorResponseData> = {
      ...buildInternalErrorDTO(errorMessage),
      config: error.config || ({} as any),
    };
    error.response = internalError;
  }

  if (
    error.response?.status &&
    error.response.status >= ErrorStatus.BUSINESS_ERROR &&
    error.response.status !== ErrorStatus.NOT_FOUND
  ) {
    error.response.data.message = parseSpecialErrors(error, language);
  }

  return Promise.reject(error.response?.data ?? error);
};

export const customFetch = async (url: string, config?: RequestConfig): Promise<any> => {
  try {
    const response = await axiosInstance.request({ url, method: 'GET', ...config });
    return response.data;
  } catch (e) {
    throw e;
  }
};
